import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { UserDb } from 'services/user-db.service';
import { LogRecordType, LogRecordState } from '@cf-shared/data-model/user-log';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { getTrainingLevels, TrainingLevel } from '@cf-shared/data-model/training-level';
import { convertScheduleDaysToRules, generateScheduleWithDefaults, Schedule, Schedule_Day, ScheduleRuleRepeat } from '@cf-shared/data-model/schedule';
import { ExerciseDbService } from 'services/exercise-db.service';
import { DateTime } from 'luxon';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { WorkoutSolverParams, WorkoutSolverSolved, ExerciseId, MuscleFactors } from '@cf-shared/workout-solver-interface';
import { requireWorkoutProgram } from 'services/server/workout-solver-failure';
import { deepClone, deepOverwriteCouchSafe } from '@cf-shared/deep-helpers';
import {
  IntensityLevel,
  ExerciseIntensities,
  Plan_Workout,
  Plan_SubSlot,
  carrySubSlotStateAcrossRebuild,
  FitnessPlan,
  WelcomePhase,
  parseSubslotIdAndOffset,
} from '@cf-shared/data-model/fitness-plan';
import { getEffectivePlanSetCount, setPlanSetCount } from '@cf-shared/data-model/plan-set-count';
import { DebugService } from 'services/debug.service';
import { suggestAvailableExercise } from 'services/exercise-switching';
import { CheckForPlanAdjustmentsResult, CheckForPlanAdjustmentsSolved, GenerateScheduleResult, PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS, PlanUpdateOutcome, PlanUpdateParams, PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { DatabaseWrapper, DocWatcher, TransactionId } from 'services/database-wrapper';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { TelemetryService } from 'services/telemetry.service';
import { LoggingService } from 'services/logging.service';
import { PlanUpdate } from '@cf-shared/plan-update';
import { Log_WeighIn } from '@cf-shared/data-model/log-weigh-in';
import { DeclaredState, FitnessState, ImmutableState } from '@cf-shared/data-model/user-state';
import { PlanCheckFailedError } from 'services/server/plan-check-failure';

export interface FakePlanParams {
  slot: string;
  sets: number | undefined;
  level: TrainingLevel;
  /** All other slots will be put at this level. This allows progress between levels to be triggered during testing, as it
   * requires at least two slots for the body half to be progressed.
   */
  otherLevel: TrainingLevel;
  intensity: IntensityLevel;
  slotIntensities: ExerciseIntensities;
}

export interface GenerateBacklogResult {
  error?: string,
  message?: string,
  schedule: Schedule_Day[],
}

@Injectable()
export class SvPlanHandlerService {

  constructor(
    public userDb: UserDb,
    public exerciseDb: ExerciseDbService,
    public cloudFunctionsService: CloudFunctionsService,
    private debug: DebugService,
    public alertCtrl: AlertController,
    private telemetry: TelemetryService,
    private logging: LoggingService,
  ) {
  }

  async handleIntakeFormAsync(transaction: TransactionId) {
    void transaction;
  }

  /**
   * Mark the plan's nutrition baseline as stale, so the next solve works the daily calorie
   * target out afresh.
   *
   * This does NOT discard a typed target the user has parked — see discardPendingCalorieTarget
   * for that, and note the difference is load-bearing: this one is also called while building
   * the throwaway max-effort plan used only to suggest values, which happens before the user
   * has been asked anything. Discarding their number there would take it away unasked.
   *
   * Call it on a copy, never on the live plan singleton: a mark left there is stored by the
   * next unrelated save and freezes the weekly checkup (#4602).
   */
  async markPlanForInitializeBaseline(plan: FitnessPlan) {
    plan.shouldInitializeBaselineAtNextReplan = true;
  }

  /**
   * Throw away a daily calorie target the user typed but has not had applied yet.
   *
   * Needed wherever the target is about to be worked out afresh: on a solve able to apply a
   * parked request, leaving one behind means the fresh number is computed and then immediately
   * replaced by the parked one.
   *
   * Call it ONLY where the user's "update my calorie target" answer is in hand, and prefer a
   * clone over the live plan — discarding somebody's number is not something to do to the
   * stored plan before they have confirmed.
   *
   * Why the solve does that, and what the screen has promised by then:
   * design-docs/nutrition-target-persistence.md.
   */
  async discardPendingCalorieTarget(plan: FitnessPlan) {
    delete plan.overrideDailyAverageIntake;
  }

  /**
   * The opposite of markPlanForInitializeBaseline: make the next solve leave the plan's
   * daily calorie target exactly where it is (#4973).
   *
   * PlanUpdate.solve can replace the target through several separate doors, and every line
   * here is needed because each alone leaves one open. One door is the nutrition anchor, which
   * this deliberately does not touch — it only has to avoid clearing it, and the one caller
   * that would have cleared it (a plan rebuild) skips that when keeping the target.
   *
   * What each door is and why closing it takes this exact shape:
   * design-docs/nutrition-target-persistence.md.
   */
  keepNutritionTargetThroughNextSolve(plan: FitnessPlan) {
    delete plan.shouldInitializeBaselineAtNextReplan;
    plan.skipNextNutritionAdjustment = true;
  }

  async replaceCurrentFitnessPlanAsync(transaction: TransactionId, newPlan: FitnessPlan) {
    await this.userDb.createCurrentPlanBackupLog(transaction);

    await this.userDb.database.withDocWatcherAsync(async watcher => {
      const fitnessPlan = await this.userDb.loadPlanAsync(watcher);
      deepOverwriteCouchSafe(fitnessPlan, newPlan);
      // df-224ad165d843: hand the save the object we just wrote into, so it still persists
      // this content if the database was re-opened underneath us and now holds a different
      // object for the same document.
      await this.userDb.savePlanAsync(transaction, fitnessPlan);
      await this.userDb.clearBacklogCacheAsync();
    });
  }

  async generateFitnessPlanAsync(mode: 'new' | 'change', declaredState: DeclaredState, fitnessState: FitnessState, immutableState: ImmutableState, options?: { fakePlan?: FakePlanParams, maxEffort?: boolean, existingPlan?: FitnessPlan, keepNutritionBaseline?: boolean }): Promise<FitnessPlan> {
    return this.userDb.database.withDocWatcherAsync(watcher =>
      this._generateFitnessPlanAsyncImpl(mode, declaredState, fitnessState, immutableState, options, watcher));
  }

  private async _generateFitnessPlanAsyncImpl(mode: 'new' | 'change', declaredState: DeclaredState, fitnessState: FitnessState, immutableState: ImmutableState, options: { fakePlan?: FakePlanParams, maxEffort?: boolean, existingPlan?: FitnessPlan, keepNutritionBaseline?: boolean }, watcher: DocWatcher): Promise<FitnessPlan> {
    // this function needs to operate on a copy of the plan, because it will be modified
    // the modified plan will be returned and the caller can decide whether to overwrite the original plan with it.
    // when an existingPlan is supplied (coach mode), use it instead of reading from userDb —
    // otherwise this implicitly loads the coach's currentplan and bleeds its subslots into the
    // client's slot plan via the oldSubSlots re-seed paths below (#3289).
    const plan = deepClone(options?.existingPlan ?? await this.userDb.loadPlanAsync(watcher));

    const now_utc = this.debug.getNowLocal().toUTC().toISO();

    if (mode === 'new') {
      plan.lastCheckup_utc = now_utc;
    }

    // mode has to be one of the valid values, also, if there's no current plan it can only be "new"
    if (!plan.ready) {
      mode = 'new';
    }

    if (mode === 'new' || mode === 'change') {
      // Keeping the user's daily calorie target across a rebuild takes all three of
      // these, because each one on its own still lets the solve replace the number.
      // Which three, and why each is needed: design-docs/nutrition-target-persistence.md.
      if (options?.keepNutritionBaseline) {
        this.keepNutritionTargetThroughNextSolve(plan);
      } else {
        plan.nutritionAdjusted = undefined;
        // A rebuild works the target out afresh through THIS line — clearing the anchor makes
        // the solver treat the plan as having no baseline — whatever any staleness flag says.
        // So a parked typed target has to go here too, or #5181 puts it straight back over the
        // number just computed. On the clone, so nothing is taken from the stored plan.
        delete plan.overrideDailyAverageIntake;
      }
      plan.startPoint = {
        timestamp_utc: now_utc,
        BW_kg: fitnessState.weight_kg,
        BF_pc: fitnessState.bfData.bf_pc, // this might not have been set yet in case of BF from photos, but will be updated later
      };
    }

    let trainingLevels = getTrainingLevels(declaredState.declaredStrength, plan, mode);

    // clear it out, but remember the old slots for a bit, and copy over the WelcomePhase
    let oldSubSlots = plan.subSlots;
    let welcomePhase = plan.welcomePhase;
    Object.assign(plan, this.userDb.getEmptyPlan());
    plan.welcomePhase = welcomePhase;

    let solverParams: WorkoutSolverParams = {
      immutableState: immutableState,
      declaredState: declaredState,
      trainingLevels: trainingLevels,
      fitnessState: fitnessState,
      fitnessPlan: plan,
      // spread sets across more movements (#4591 follow-up); the server still honours a
      // false from older builds, see WorkoutSolverParams.spreadSetsAcrossMovements
      spreadSetsAcrossMovements: true,
    };

    const fakePlan = options && options.fakePlan;

    // throws when there is no program; a paid-feature refusal throws WorkoutSolverRefusedError
    let solverResults = requireWorkoutProgram(await this.cloudFunctionsService.solveWorkoutsAsync(solverParams), "Can't solve workout");

    // in case of manual testing of progression loops
    if (fakePlan) {
      // find proper intensities for desired slot...
      let intensities: ExerciseIntensities = null;

      // use custom intensities, if set
      if (fakePlan.slotIntensities) {
        intensities = fakePlan.slotIntensities;

        // otherwise, try to find the slot in the actual generated program and copy the intensities from there
      } else {
        for (let day of solverResults.days) {
          for (let exercise of day) {
            if ('slot/' + exercise.subSlotId === fakePlan.slot) {
              intensities = exercise.intensities;
            }
          }
        }
      }

      // if not determined in any other way, use the first possible intensities, just to have something
      if (!intensities) {
        intensities = solverResults.days[0][0].intensities;
      }

      // overwrite the actual plan
      solverResults.days = [
        [
          {
            subSlotId: fakePlan.slot as ExerciseId,
            sets: fakePlan.sets,
            trainingLevel: fakePlan.level,
            intensities: intensities,
          },
        ],
      ];
      // set all levels to otherLevel, except the one for this slot which is set to the base level
      solverResults.trainingLevels = {
        $bench_press: fakePlan.otherLevel,
        $chin_up: fakePlan.otherLevel,
        $ohp: fakePlan.otherLevel,
        $rdl: fakePlan.otherLevel,
        $squat: fakePlan.otherLevel,
      };
      let mainSlotId = fakePlan.slot.match(/slot\/(.+)/)?.[1];
      if (mainSlotId) {
        solverResults.trainingLevels[mainSlotId] = fakePlan.level;
      }
    }

    // prepare a map for workout names based on splits...
    // (note that we can't use template names because we want them to always be just A-B-C-...)
    let letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']; // overkill, just in case

    // for each of the days in the split
    for (let dayIndex = 0; dayIndex < solverResults.days.length; dayIndex++) {
      let day = solverResults.days[dayIndex];

      // create one workout
      let workout = new Plan_Workout();
      plan.workouts.push(workout);
      workout.name = letters[dayIndex];
      workout.subSlotIds = [];

      // for each exercise slot in the workout template
      for (let iex = 0; iex < day.length; iex++) {
        let e = day[iex];
        // some slots are named as "$<slot_name>$<starting_exercise>"
        let { initialExerciseIndex, parsedSlotId } = parseSubslotIdAndOffset(e.subSlotId);
        let subSlotId = parsedSlotId + '$' + (initialExerciseIndex).toString(); // same as e.subSlotId, but normalized
        let slotId = 'slot/' + parsedSlotId;

        // find the list of exercises in that slot
        let slot = await this.exerciseDb.slotForIdAsync(slotId, watcher);
        if (slot == null) {
          console.error(`Exercise slot ${slotId} not found!`);
          continue;
        }
        if (!slot.exercises || slot.exercises.length < 1) {
          console.error(`Exercise slot ${slotId} contains no exercises!`);
          continue;
        }

        // determine which exercise to start with
        if (initialExerciseIndex > slot.exercises.length) {
          console.error(`Exercise slot ${slotId} doesn't have exercise #${initialExerciseIndex}!`);
          initialExerciseIndex = 0;
        }

        let exerciseId: string;
        let oldPlanSubSlotExerciseId: string;
        let oldSubSlot = oldSubSlots[subSlotId];
        if (oldSubSlot) {
          // See if last excericse for this subslot was still actual in last plan
          oldPlanSubSlotExerciseId = suggestAvailableExercise(
            fitnessState,
            plan,
            slot,
            initialExerciseIndex,
            oldSubSlot.exercise_id,
            this.debug.getNowLocal().toUTC(),
          );
        }
        // If last plan subslot exercise was not changed in last training session use it again in new plan if possibile
        if (oldSubSlot && oldPlanSubSlotExerciseId === oldSubSlot.exercise_id) {
          exerciseId = oldPlanSubSlotExerciseId;
        } else {
          // skip all avoided exercises
          exerciseId = suggestAvailableExercise(
            fitnessState,
            plan,
            slot,
            initialExerciseIndex,
            slot.exercises[initialExerciseIndex].exerciseId,
            this.debug.getNowLocal().toUTC(),
          );
        }

        // add that exercise and the slot to plan (if not already there)
        let subSlot = plan.subSlots[subSlotId];
        if (subSlot == null) {
          subSlot = {} as Plan_SubSlot;
          plan.subSlots[subSlotId] = subSlot;
          subSlot.slotId = slotId;
          subSlot.startIndex = initialExerciseIndex;
        }

        // eslint-disable-next-line no-restricted-syntax -- fresh subSlot construction during plan regen, not a swap
        subSlot.exercise_id = exerciseId;
        subSlot.trainingLevel = e.trainingLevel;

        subSlot.intensities = fakePlan && fakePlan.slotIntensities || e.intensities;
        subSlot.defaultVolume_sets = e.sets;

        // decide what this slot keeps from the plan being replaced. The rule is shared and
        // unit-tested in fitness-plan.ts — see carrySubSlotStateAcrossRebuild for why an
        // unchanged exercise must keep its progression and a replaced one must not.
        // if this is a fake plan (manual progression testing), hide the old slot from it so
        // every slot starts clean at the intensity the tester asked for.
        carrySubSlotStateAcrossRebuild(
          subSlot,
          fakePlan ? undefined : oldSubSlot,
          fakePlan ? fakePlan.intensity : IntensityLevel.Initial,
        );
        // store that exercise in the wo
        workout.subSlotIds.push(subSlotId);
      }
    }

    // training levels for further progress are copied directly
    plan.trainingLevels = solverResults.trainingLevels;

    // store backup of the generated plan for reverting user edits
    plan.generatedPlanBackup = {
      workouts: deepClone(plan.workouts),
      subSlots: deepClone(plan.subSlots),
    };

    plan.ready = true; // needed to know that further changes to plan are just updates
    plan.needsReplan = false; // re-planning is done now
    if (plan.welcomePhase !== WelcomePhase.Done) {
      plan.welcomePhase = WelcomePhase.Done;

      // track onboarding completion for email flow
      this.telemetry.setUserPropertiesOnce({ onboarding_finished_at: DateTime.utc().toISO() });
    }

    plan.trainingVolumeWeeklyAvg = solverResults.trainingVolumeWeeklyAvg;

    // report to telemetry
    this.telemetry.trackEvent('plan_generated', {
      mode: mode,
      is_max_effort: options && options.maxEffort,
      solver_params: deepClone(solverParams, { skipPouch: true }),
      plan: deepClone(plan, { skipPouch: true }),
    });

    return plan;
  }

  // generate initial week schedule from intake data
  async generateDefaultScheduleAsync(declaredState: DeclaredState): Promise<Schedule> {

    const ruleStartDate = this.debug.getNowLocal().toISODate();
    const schedule = deepClone(await this.userDb.loadScheduleAsync());
    let newRules = convertScheduleDaysToRules(generateScheduleWithDefaults(declaredState).slice(0, 7), ruleStartDate);
    if (schedule.rules) {
      const todayDate = this.debug.getNowLocal().toISODate();
      // We will keep one time rules for history matter.
      schedule.rules = schedule.rules.filter(rule => rule.repeat === ScheduleRuleRepeat.Never && ruleStartDate < todayDate).concat(newRules);
    } else {
      schedule.rules = newRules;
    }

    return schedule;
  }

  async handleSkipNutritionAdjustments() {
    const fitnessPlan = await this.userDb.database.withDocWatcherAsync(watcher =>
      this.userDb.loadPlanAsync(watcher));
    if (fitnessPlan.shouldInitializeBaselineAtNextReplan || !fitnessPlan.needsReadjustment || fitnessPlan.overrideDailyAverageIntake) {
      return;
    }

    await this.alertCtrl.showAlertAsync({
      message: LL.planHandler.macrosOnTarget(),
      buttons: [
        {
          text: LL.common.buttons.yes(),
          role: AlertButtonRole.Primary,
          result: async () => {
            await (async () => {
              fitnessPlan.skipNextNutritionAdjustment = undefined;
              fitnessPlan.lastTimeMissedNutritionMacros_utc = undefined;
              await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
            })();
          },
        },
        {
          text: LL.common.buttons.no(),
          role: AlertButtonRole.Cancel,
          result: async () => {
            await (async () => {
              fitnessPlan.skipNextNutritionAdjustment = true;
              fitnessPlan.lastTimeMissedNutritionMacros_utc = this.debug.getNowLocal().toUTC().toISO();
              fitnessPlan.needsReadjustment = false;
              await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
            })();
          },
        },
      ],
    });
  }

  async updatePlanAsync(fitnessPlan: FitnessPlan, declaredState: DeclaredState, fitnessState: FitnessState, immutableState: ImmutableState): Promise<{ message: PlanUpdateUserMessage; clampedToMinimumIntake: boolean; minimumDailyAverageIntake?: number }> {
    const today = this.debug.getNowLocal();
    const weightStart = today.minus({ days: 14 }).startOf('day');
    const weightEnd = today.endOf('day');

    // get all workouts in 7 days before the start
    let weighInEntries = await this.userDb.database.withDocWatcherAsync(watcher =>
      this.userDb.getLogEntriesInRangeAsync(weightStart, weightEnd, watcher));
    weighInEntries = weighInEntries.filter(e => e.type == LogRecordType.WeighIn && e.state === LogRecordState.Done && !isNaN(e.weight_kg));
    let weighIns = weighInEntries as Log_WeighIn[];

    let solver = new PlanUpdate();
    let result = solver.solve(deepClone({
      immutableState: immutableState,
      declaredState,
      fitnessState: fitnessState,
      fitnessPlan,
      schedule: undefined,
      weighIns,
      historyLogs: [],
      today_local: today.toISO(),
    }));
    if (result.outcome === PlanUpdateOutcome.Failed) {
      throw new Error(result.error);
    }

    // if the solver found a stored energy balance whose sign contradicted the goal
    // direction (e.g. a cutting deficit carried across a switch to muscle growth) it
    // healed it with the goal-consistent default — report that so it stays visible
    // in Sentry (shared code has no Sentry runtime of its own, #4037)
    if (result.healedIntakeDelta) {
      this.logging.trackSilentInfo('plan-update-healed-inconsistent-intake-delta', {
        storedDelta: result.healedIntakeDelta.stored,
        usedDelta: result.healedIntakeDelta.used,
        adjustmentAction: result.healedIntakeDelta.action,
        declaredIntakeDeltaPercent: declaredState.intakeDeltaPercent,
        trainingGoalGrowth: declaredState.trainingGoalGrowth,
      });
      // if the poison source was the PERSISTED declared state, repair it too — the
      // solver reads the declared value first on every solve, so leaving it in place
      // would re-trigger this heal (and the canary above) on every future adjusting
      // solve and the user would never actually recover. Only do this when the caller
      // passed the cached declared singleton (weekly checkup, program settings) —
      // the intake form passes throwaway clones projected from in-progress form
      // state, and persisting those is the form's own save flow's responsibility.
      if (declaredState.intakeDeltaPercent !== undefined) {
        const persistedDeclaredState = await this.userDb.database.withDocWatcherAsync(watcher =>
          this.userDb.loadDeclaredStateAsync(watcher));
        if (persistedDeclaredState === declaredState) {
          declaredState.intakeDeltaPercent = result.healedIntakeDelta.used * 100;
          await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
        }
      }
    }

    // fitnessPlan singletons, this is the only way to replace them
    deepOverwriteCouchSafe(fitnessPlan, result.fitnessPlan);

    return {
      message: result.message,
      clampedToMinimumIntake: result.clampedToMinimumIntake === true,
      // the lowest target this solve would set (#5456); callers that do not warn about it ignore it
      minimumDailyAverageIntake: result.minimumDailyAverageIntake,
    };
  }

  async checkForPlanAdjustments(): Promise<CheckForPlanAdjustmentsSolved> {
    return this.userDb.database.withDocWatcherAsync(async watcher => {
      // load current plan and input data
      const [immutableState, declaredState, fitnessState, fitnessPlan] = await Promise.all([
        this.userDb.loadImmutableStateAsync(watcher),
        this.userDb.loadDeclaredStateAsync(watcher),
        this.userDb.loadFitnessStateAsync(watcher),
        this.userDb.loadPlanAsync(watcher),
      ]);

      const today = this.debug.getNowLocal();
      const weightStart = today.minus({ days: PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS }).startOf('day');
      const weightEnd = today.endOf('day');

      // get all workouts in 7 days before the start
      const weighInEntries = (await this.userDb.getLogEntriesInRangeAsync(weightStart, weightEnd, watcher)).filter(e => e.type == LogRecordType.WeighIn && e.state === LogRecordState.Done && !isNaN(e.weight_kg));
      const weighIns = weighInEntries as Log_WeighIn[];

      const historyLogsStart = today.minus({ days: PLAN_ADJUSTMENT_INPUT_WINDOW_DAYS }).startOf('day');
      const historyLogsEnd = today.endOf('day');
      const historyLogs = (await this.userDb.getLogEntriesInRangeAsync(historyLogsStart, historyLogsEnd, watcher)).filter(
        e => e.type === LogRecordType.Workout || e.type === LogRecordType.Meal,
      );

      let solver = new PlanUpdate();
      const result = solver.checkForPlanAdjustments({
        immutableState,
        declaredState,
        fitnessState,
        fitnessPlan,
        schedule: undefined,
        weighIns,
        historyLogs: historyLogs,
        today_local: today.toISO(),
      });

      // `PlanUpdate` answers a crash with a failed result rather than by throwing. Before #5776 that
      // result still carried plausible-looking verdict fields -- `needsAdjustment` false and
      // `message` usually `AllOk` -- so a caller reading only those rendered a failed check as the
      // POSITIVE verdict: "you are on track". The failed branch now carries no verdict at all.
      //
      // Turn it into a failure here, in the one place every caller goes through, rather than
      // handing back a verdict nobody computed. Reporting it (which is what df-c73768bb383e added)
      // made the failure visible to us; throwing is what stops it being shown to the user as an
      // answer about their own progress, and it means a caller added later gets that for free
      // instead of having to know that `error` exists. See design-docs/telemetry.md for the
      // severity tiers, and df-5ba092626ea2 for the four screens this was decided against.
      if (result.outcome === PlanUpdateOutcome.Failed) {
        this.logging.trackSilentProblem('plan-update-check-for-adjustments-failed', { error: result.error });
        throw new PlanCheckFailedError(result.error);
      }

      return result;
    });
  }

  /** This is an old version of the checkForPlanAdjustments, which is kept for compatibility with the action cards service.
   * It should be removed once the action cards service is updated to use the new version.
   */
  checkForPlanAdjustmentsOld(params: PlanUpdateParams): CheckForPlanAdjustmentsResult {
    let solver = new PlanUpdate();
    let result = solver.checkForPlanAdjustments(params);
    return result;
  }

  public async generateScheduleAsync(params: PlanUpdateParams): Promise<GenerateScheduleResult> {
    let paramsClone = deepClone(params);
    paramsClone.historyLogs = paramsClone.historyLogs.filter(e => e.type === LogRecordType.Workout || e.type === LogRecordType.Meal);
    paramsClone.historyLogs.map(e => {
      if (e.type === LogRecordType.Meal) {
        e.nutrients = {};
      }
    });

    let solver = new PlanUpdate();
    let result = solver.generateSchedule(paramsClone);
    return result;
  }

  public createWorkoutEntry(time: DateTime): Log_Workout {
    return this.userDb.createWorkoutEntry(time);
  }

  public async reoptimizeVolumeAsync(
    fitnessPlan: FitnessPlan,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    immutableState: ImmutableState,
    muscleFactorOverrides?: { [subSlotId: string]: MuscleFactors },
    usesMyoRepsBySubSlotId?: { [subSlotId: string]: boolean },
    muscleFactorSourceSlots?: { [subSlotId: string]: string },
  ): Promise<{ changes: VolumeChange[]; solverResults: WorkoutSolverSolved }> {
    // extract fixedExercises from the plan's workouts
    const fixedExercises: { subSlotId: ExerciseId; dayIndex: number }[] = [];
    for (let dayIndex = 0; dayIndex < fitnessPlan.workouts.length; dayIndex++) {
      const workout = fitnessPlan.workouts[dayIndex];
      for (const subSlotId of workout.subSlotIds) {
        fixedExercises.push({ subSlotId: subSlotId as ExerciseId, dayIndex });
      }
    }

    const trainingLevels = getTrainingLevels(declaredState.declaredStrength, fitnessPlan, 'change');

    const solverParams: WorkoutSolverParams = {
      immutableState,
      declaredState,
      trainingLevels,
      fitnessState,
      fitnessPlan,
      volumeOnlyMode: true,
      fixedExercises,
      muscleFactorOverrides,
      muscleFactorSourceSlots,
      // spread sets across more movements (#4591 follow-up); the server still honours a
      // false from older builds, see WorkoutSolverParams.spreadSetsAcrossMovements
      spreadSetsAcrossMovements: true,
    };

    const solverResults = requireWorkoutProgram(await this.cloudFunctionsService.solveWorkoutsAsync(solverParams), "Can't reoptimize volume");

    // compare old vs new sets (skip exercises the solver couldn't handle — they get 0 but shouldn't be shown as changes)
    const changes: VolumeChange[] = [];
    for (const day of solverResults.days) {
      for (const exercise of day) {
        // if solver assigned 0 sets, it means the exercise has no LP representation (custom without similar exercise) — skip
        if (exercise.sets === 0) continue;
        const subSlot = fitnessPlan.subSlots[exercise.subSlotId];
        // compare against the count the user's workouts really hold, so a set they gained
        // by accepting the app's volume suggestion is not reported as a change the
        // optimizer is making (#4523)
        const oldSets = subSlot
          ? getEffectivePlanSetCount(subSlot, !!usesMyoRepsBySubSlotId?.[exercise.subSlotId])
          : 0;
        if (oldSets !== exercise.sets) {
          changes.push({ subSlotId: exercise.subSlotId, oldSets, newSets: exercise.sets });
        }
      }
    }

    return { changes, solverResults };
  }

  public applyVolumeChanges(fitnessPlan: FitnessPlan, changes: VolumeChange[]): void {
    for (const change of changes) {
      if (fitnessPlan.subSlots[change.subSlotId]) {
        // each optimized number becomes the plan's own count, and the volume adjustment
        // agreed during training is cleared rather than left to be added on top of it
        // (#4523). Why clearing and not subtracting, and why rebuilding the whole program
        // deliberately does the opposite, are in design-docs/volume-autoregulation.md.
        //
        // Note this is NOT always a number the user was shown and confirmed: the Muscle
        // Volume page asks, but the intake form's "volumes only" path applies straight
        // away (client/src/pages/intake-form/intake-form.ts).
        setPlanSetCount(fitnessPlan.subSlots[change.subSlotId], change.newSets);
      }
    }
  }
}

export interface VolumeChange {
  subSlotId: string;
  oldSets: number;
  newSets: number;
}
